import React, { useEffect, useState, useCallback } from 'react';
import { ChevronDown, ChevronRight, MessageSquarePlus, Edit, Trash2, X, Check } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const formatRelative = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const fullTimestamp = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

const ProjectNotes = ({ projectId, currentUserId }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newText, setNewText] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/projects/${projectId}/notes`);
      setNotes(res.data);
    } catch {
      toast.error('Failed to load notes');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (projectId) fetchNotes();
  }, [projectId, fetchNotes]);

  const handleAdd = async () => {
    const text = newText.trim();
    if (!text) return;
    setSubmitting(true);
    try {
      const res = await api.post(`/api/projects/${projectId}/notes`, { text });
      setNotes([res.data, ...notes]);
      setNewText('');
      toast.success('Note added');
    } catch {
      toast.error('Failed to add note');
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (note) => {
    setEditingId(note.note_id);
    setEditText(note.text);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditText('');
  };

  const handleSaveEdit = async () => {
    const text = editText.trim();
    if (!text) return;
    setSubmitting(true);
    try {
      const res = await api.put(`/api/projects/${projectId}/notes/${editingId}`, { text });
      setNotes(notes.map(n => n.note_id === editingId ? res.data : n));
      cancelEdit();
      toast.success('Note updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update note');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (note_id) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await api.delete(`/api/projects/${projectId}/notes/${note_id}`);
      setNotes(notes.filter(n => n.note_id !== note_id));
      toast.success('Note deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete note');
    }
  };

  return (
    <div className="card p-4 sm:p-6" data-testid="project-notes-section">
      <button
        onClick={() => setCollapsed(c => !c)}
        className="flex items-center gap-2 hover:text-gold transition-colors w-full justify-between sm:justify-start"
        data-testid="notes-toggle"
      >
        <span className="flex items-center gap-2">
          {collapsed ? <ChevronRight size={18} /> : <ChevronDown size={18} />}
          <h2 className="text-xl sm:text-2xl font-serif">Project Notes ({notes.length})</h2>
        </span>
      </button>

      {!collapsed && (
        <div className="mt-4 space-y-3" data-testid="notes-list">
          {/* Quick-add */}
          <div className="border border-dashed border-gold/40 p-3">
            <div className="flex items-start gap-2">
              <MessageSquarePlus size={18} className="text-gold flex-shrink-0 mt-2" />
              <textarea
                value={newText}
                onChange={(e) => setNewText(e.target.value)}
                placeholder="Add a note (e.g., 'Bride wants sunset photos at 4:30pm')..."
                rows={2}
                className="flex-1 px-3 py-2 text-sm border border-input bg-background resize-none"
                data-testid="note-new-input"
                onKeyDown={(e) => {
                  if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') handleAdd();
                }}
              />
            </div>
            <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
              <span className="text-[11px] text-muted-foreground">
                ⌘/Ctrl + Enter to post
              </span>
              <button
                onClick={handleAdd}
                disabled={!newText.trim() || submitting}
                className="px-3 py-1.5 text-xs bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="note-add-btn"
              >
                ADD NOTE
              </button>
            </div>
          </div>

          {/* Notes list */}
          {loading ? (
            <p className="text-sm text-muted-foreground text-center py-4">Loading notes…</p>
          ) : notes.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4" data-testid="notes-empty">
              No notes yet. Add the first one above ↑
            </p>
          ) : (
            <div className="space-y-2">
              {notes.map((note) => {
                const isOwn = note.author_id === currentUserId;
                const isEditing = editingId === note.note_id;
                const edited = note.updated_at && note.updated_at !== note.created_at;
                return (
                  <div
                    key={note.note_id}
                    className={`border p-3 ${isOwn ? 'border-gold/40 bg-gold/5' : 'border-border'}`}
                    data-testid={`note-${note.note_id}`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className={`font-medium ${isOwn ? 'text-gold' : 'text-foreground'}`}>
                          {note.author_name || 'Admin'}
                        </span>
                        <span title={fullTimestamp(note.created_at)}>
                          · {formatRelative(note.created_at)}
                        </span>
                        {edited && (
                          <span className="text-[10px] italic" title={`Edited ${fullTimestamp(note.updated_at)}`}>
                            (edited)
                          </span>
                        )}
                      </div>
                      {isOwn && !isEditing && (
                        <div className="flex gap-1">
                          <button
                            onClick={() => startEdit(note)}
                            className="p-1 hover:text-gold transition-colors"
                            title="Edit"
                            data-testid={`note-edit-${note.note_id}`}
                          >
                            <Edit size={12} />
                          </button>
                          <button
                            onClick={() => handleDelete(note.note_id)}
                            className="p-1 hover:text-red-500 transition-colors"
                            title="Delete"
                            data-testid={`note-delete-${note.note_id}`}
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      )}
                    </div>
                    {isEditing ? (
                      <>
                        <textarea
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          rows={Math.max(2, editText.split('\n').length)}
                          className="w-full px-3 py-2 text-sm border border-input bg-background resize-none"
                          autoFocus
                          data-testid={`note-edit-input-${note.note_id}`}
                        />
                        <div className="flex gap-1 mt-2">
                          <button
                            onClick={handleSaveEdit}
                            disabled={submitting}
                            className="px-2 py-1 text-xs bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 flex items-center gap-1"
                          >
                            <Check size={12} /> SAVE
                          </button>
                          <button
                            onClick={cancelEdit}
                            className="px-2 py-1 text-xs border border-border hover:bg-accent flex items-center gap-1"
                          >
                            <X size={12} /> CANCEL
                          </button>
                        </div>
                      </>
                    ) : (
                      <p className="text-sm whitespace-pre-wrap break-words">{note.text}</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProjectNotes;
